import { planTypes, PlanModelInfo, PBX_RELATED_PLAN_TYPES, BILLING_CYCLE } from '@/common/constants';
import { getCartInfo } from "@/common/service/default-mapper";
import { planUnitMap } from '@/common/product-unit';
import _ from 'lodash';
import { getZvaMinutesCode } from '@/plans/ZOOM_PHONE$AICONCIERGE/helper/parse';

/**
 * Zoom Virtual Agent Receptionist - standalone usage plan.
 * Backend returns two sibling nodes:
 * - planMapJSON.zva_concierge: prepaid plan with tier prices
 * - planMapJSON.zva_concierge_pyg: pay-as-you-go plan
 *
 * Supported shapes:
 * - Array: node.plans = [...]
 * - Object: nested object which contains plan objects
 */
export default (planMapJSON) => {

  const PREPAY_MINUTES_OPTIONS = [500, 1000, 2500];

  const collectPlansDeep = (node) => {
    // Collect any object that looks like a plan: has plan_code (or planCode).
    // ZVA backend sometimes nests like: zva_concierge_pyg: { <plan_key>: { plan_code... } }
    const out = [];
    const walk = (val) => {
      if (!val) return;
      if (Array.isArray(val)) {
        val.forEach(walk);
        return;
      }
      if (typeof val !== 'object') return;
      if (val.plan_code || val.planCode) {
        out.push(val);
        return;
      }
      Object.values(val).forEach(walk);
    };
    walk(node);
    return out;
  };

  const rawPrepayNode = planMapJSON.zva_concierge || {};
  const rawPaygNode = planMapJSON.zva_concierge_pyg || {};

  const prepayPlans = Array.isArray(rawPrepayNode?.plans) ? rawPrepayNode.plans : collectPlansDeep(rawPrepayNode);
  const paygPlans = Array.isArray(rawPaygNode?.plans) ? rawPaygNode.plans : collectPlansDeep(rawPaygNode);

  const rawPlans = [...paygPlans, ...prepayPlans];

  const isPaygPlan = (p) => {
    if (p?.is_payg === true) return true;
    const code = (p?.plan_code || '').toString().toLowerCase();
    return code.includes('pay_as_you_go') || code.includes('payg') || code.includes('pyg');
  };

  const findUnitPriceCentsByUsage = (prices = [], usage = 0) => {
    if (!Array.isArray(prices) || prices.length === 0) return 0;
    // Pick the most specific tier: among tiers that match usage, choose the one with max start.
    const matched = prices.filter((t) => _.isNumber(t?.start) && _.isNumber(t?.end) && t.start <= usage && t.end >= usage);
    if (matched.length === 0) return 0;
    const best = _.maxBy(matched, (t) => t.start);
    return _.toNumber(best?.price) || 0;
  };

  const paygRaw = rawPlans.find(isPaygPlan) || null;
  const prepayRaw = rawPlans.find((p) => !isPaygPlan(p) && Array.isArray(p?.prices) && p.prices.length) || null;

  // Pay as you go unit price (cents). If backend doesn't provide it directly, fall back to prepay tier start=0.
  const paygUnitPriceCents =
    (_.toNumber(paygRaw?.amount) || 0) ||
    findUnitPriceCentsByUsage(prepayRaw?.prices, 0);

  const baseCommon = (raw) => ({
    type: planTypes.ZOOM_PHONE$AICONCIERGE,
    category: raw?.category || raw?.category_code,
    plan_type: raw?.plan_type,
    cartInfo: getCartInfo(),
    min: 1,
    max: 1,
    billing_cycle: raw?.billing_cycle || BILLING_CYCLE.MONTHLY,
    isOnlineFreeTrial: raw?.isOnlineFreeTrial || false,
    trialDays: raw?.trialDays,
    trialCode: raw?.trialCode,
    campaignCode: raw?.campaignCode,
    dependBasePlanTypeList: [...PBX_RELATED_PLAN_TYPES],
    supportCoupon: true,
    typeNameKey: 'billing.planform.title.zoom_phone_ai_concierge',
    planNameKey: 'billing.planform.title.zoom_phone_ai_concierge',
    planModel: PlanModelInfo.usage,
    unit: planUnitMap.perMinute,
  });

  const result = [];

  if (paygRaw?.plan_code) {
    result.push({
      ...baseCommon(paygRaw),
      plan_code: paygRaw.plan_code,
      amount: paygUnitPriceCents,
      is_payg: true,
      is_prepay: false,
    });
  }

  if (prepayRaw?.plan_code && Array.isArray(prepayRaw?.prices) && prepayRaw.prices.length) {
    const basePlanCode = prepayRaw.plan_code;
    PREPAY_MINUTES_OPTIONS.forEach((mins) => {
      const unitPriceCents = findUnitPriceCentsByUsage(prepayRaw.prices, mins);
      result.push({
        ...baseCommon(prepayRaw),
        plan_code: getZvaMinutesCode(basePlanCode, mins),
        base_plan_code: basePlanCode,
        minutes: mins,
        amount: unitPriceCents,
        totalAmount: unitPriceCents * mins,
        is_payg: false,
        is_prepay: true,
      });
    });
  }

  return result;
};
